/* Enhance Dawn's native variant and purchase flow; no independent variant database. */
if (!customElements.get('strayzy-phone-model')) {
  customElements.define('strayzy-phone-model', class extends HTMLElement {
    connectedCallback() {
      this.select = this.querySelector('select');
      this.brands = this.querySelector('[data-phone-brands]');
      if (!this.select || !this.brands) return;
      this.modelOptions = [...this.select.options];
      this.buttons = [...this.querySelectorAll('[data-phone-family]')];
      const families = new Set(this.modelOptions.map(option => this.family(option.value)));
      // A non-phone option keeps its normal dropdown; never hide unknown products.
      if (!families.has('iphone') && !families.has('samsung')) return;
      this.buttons.forEach(button => { button.hidden = !families.has(button.dataset.phoneFamily); });
      const current = this.select.selectedOptions[0];
      if (!current) return;
      this.filter(this.family(current.value), current.value);
      this.brands.hidden = false;
      this.controller = new AbortController();
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-phone-family]');
        if (!button || button.hidden || button.getAttribute('aria-pressed') === 'true') return;
        this.filter(button.dataset.phoneFamily);
        // Keep Shopify's real option IDs, availability, pricing and image updates.
        // Dawn requires a selected attribute before its native change handler runs.
        this.select.dataset.brandTrigger = button.id;
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
        delete this.select.dataset.brandTrigger;
      }, { signal: this.controller.signal });
    }
    family(value) {
      if (/iphone/i.test(value)) return 'iphone';
      if (/samsung|galaxy/i.test(value)) return 'samsung';
      return 'other';
    }
    modelRank(value) {
      const name = value.toLowerCase();
      const numbered = name.match(/iphone\s*(\d+)|(?:galaxy\s*)?s(\d+)/);
      let generation = numbered ? Number(numbered[1] || numbered[2]) : -1;
      // Named iPhone generations present in the current product catalog.
      if (/iphone\s+air\b/.test(name)) generation = 17;
      else if (/iphone\s+x[rs]\b/.test(name)) generation = 10.5;
      else if (/iphone\s+x\b/.test(name)) generation = 10;
      const tier = /ultra|pro max/.test(name) ? 5 : /pro/.test(name) ? 4 : /plus|\+|max/.test(name) ? 3 : /mini|\bair\b/.test(name) ? 0 : 1;
      return generation * 10 + tier;
    }
    filter(family, preferredValue) {
      const options = this.modelOptions.filter(option => this.family(option.value) === family)
        .sort((a, b) => this.modelRank(b.value) - this.modelRank(a.value));
      const selected = options.find(option => option.value === preferredValue) || options[0];
      if (!selected) return;
      this.modelOptions.forEach(option => {
        option.selected = option === selected;
        if (option === selected) option.setAttribute('selected', 'selected');
        else option.removeAttribute('selected');
      });
      // Remove other families, rather than relying on hidden <option> support on iOS.
      this.select.replaceChildren(...options);
      this.select.value = selected.value;
      this.buttons.forEach(button => {
        button.setAttribute('aria-pressed', String(button.dataset.phoneFamily === family));
      });
    }
    disconnectedCallback() { this.controller?.abort(); }
  });
}

if (!customElements.get('strayzy-gallery-controls')) {
  customElements.define('strayzy-gallery-controls', class extends HTMLElement {
    connectedCallback() {
      this.gallery = this.closest('media-gallery');
      this.rail = this.gallery.querySelector('.thumbnail-list');
      if (!this.rail) return;
      this.controller = new AbortController();
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-gallery-direction]');
        if (!button) return;
        const items = this.items;
        const index = items.findIndex(item => item.querySelector('[aria-current="true"]'));
        const next = items[index + Number(button.dataset.galleryDirection)];
        if (next) this.gallery.setActiveMedia(next.dataset.target, false);
      }, { signal: this.controller.signal });
      this.observer = new MutationObserver(() => this.sync());
      this.observer.observe(this.rail, { subtree: true, attributes: true, attributeFilter: ['aria-current'], childList: true });
      this.sync();
    }
    get items() { return [...this.rail.querySelectorAll('[data-target]')].filter(item => getComputedStyle(item).display !== 'none'); }
    sync() {
      const items = this.items;
      const index = items.findIndex(item => item.querySelector('[aria-current="true"]'));
      this.hidden = items.length < 2;
      this.querySelector('[data-gallery-direction="-1"]').disabled = index <= 0;
      this.querySelector('[data-gallery-direction="1"]').disabled = index >= items.length - 1;
      // Dawn handles media, video, announcements and horizontal thumbnails.
      // Only the desktop vertical thumbnail rail needs a different scroll axis.
      if (matchMedia('(min-width: 1200px)').matches && items[index]) {
        const item = items[index].getBoundingClientRect();
        const rail = this.rail.getBoundingClientRect();
        if (item.top < rail.top || item.bottom > rail.bottom) {
          this.rail.scrollTop += item.top - rail.top;
        }
      }
    }
    disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); }
  });
}

if (!customElements.get('strayzy-product-addon')) {
  customElements.define('strayzy-product-addon', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const select = this.querySelector('[data-addon-variant]');
      const submit = this.querySelector('[type=submit]');
      if (select && submit) {
        this.pendingObserver = new MutationObserver(() => {
          select.disabled = submit.getAttribute('aria-disabled') === 'true';
        });
        this.pendingObserver.observe(submit, { attributes: true, attributeFilter: ['aria-disabled'] });
      }
      this.querySelector('[data-addon-variant]')?.addEventListener('change', event => {
        const option = event.target.selectedOptions[0];
        this.querySelector('[data-addon-price]').textContent = option.dataset.price;
        this.querySelector('[name=quantity]').value = option.dataset.minimum || '1';
        const image = this.querySelector('img');
        if (image && option.dataset.image) {
          image.removeAttribute('srcset');
          image.src = option.dataset.image;
        }
        this.querySelector('product-form')?.handleErrorMessage?.();
      }, { signal: this.controller.signal });
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.pendingObserver?.disconnect();
    }
  });
}


if (!customElements.get('strayzy-product-tools')) {
  customElements.define('strayzy-product-tools', class extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('product-info');
      this.form = this.root.querySelector('product-form');
      this.button = this.form?.querySelector('[type=submit]');
      this.bar = this.querySelector('[data-sticky-buy]');
      if (!this.button) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.form.addEventListener('submit', event => {
        if (!this.root.querySelector('[data-excluded-selection]')) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.root.querySelector('.strayzy-product__option--visual input')?.focus();
      }, { capture: true, signal });
      this.root.addEventListener('change', event => {
        if (event.target.matches('.product-variant-id')) {
          this.root.removeAttribute('aria-busy');
          clearTimeout(this.failureTimer);
          queueMicrotask(() => this.restorePickerFocus());
          return;
        }
        if (!event.target.closest('variant-selects') || event.target.matches('[data-option-search]')) return;
        this.root.setAttribute('aria-busy', 'true');
        this.root.querySelector('[data-variant-error]')?.setAttribute('hidden', '');
        this.lastPickerId = event.target.dataset.brandTrigger || event.target.id;
        this.lastPickerElement = event.target.dataset.brandTrigger ? document.activeElement : event.target;
      }, { signal });
      // A narrow observer follows native button state, including unavailable variants.
      this.stateObserver = new MutationObserver(() => this.sync());
      this.stateObserver.observe(this.form, { subtree: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled'], childList: true, characterData: true });
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (data.sectionId !== this.dataset.section) return;
        this.root.removeAttribute('aria-busy');
        this.sync();
      });
      // Native fetch failures must be actionable, without replacing Dawn's data flow.
      this.onSelect = ({ data }) => {
        if (!this.root.contains(data.event.target)) return;
        const picker = this.root.querySelector('variant-selects');
        const pending = picker?.pendingSelectPromise?.promise;
        pending?.catch(error => { if (error.name !== 'AbortError') this.showVariantError(); });
        clearTimeout(this.failureTimer);
        this.failureTimer = setTimeout(() => {
          if (this.root.getAttribute('aria-busy') === 'true') this.showVariantError();
        }, 12000);
      };
      this.unsubscribeOptions = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, this.onSelect);
      this.querySelector('[data-buy-shortcut]')?.addEventListener('click', () => {
        const target = this.root.querySelector('variant-selects') || this.button;
        target.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
        const focus = target.querySelector('summary, input:checked, select') || this.button;
        focus.focus({ preventScroll: true });
      }, { signal });
      this.visibilityObserver = new IntersectionObserver(() => {
        this.pastButton = this.button.getBoundingClientRect().bottom < 0;
        this.sync();
      });
      this.visibilityObserver.observe(this.button);
      // Watching the section also catches jumps from below to above the button
      // without the button ever intersecting (e.g. End or an anchor link).
      this.visibilityObserver.observe(this.root);
      this.root.addEventListener('focusin', () => this.sync(), { signal });
      this.root.addEventListener('focusout', () => queueMicrotask(() => this.sync()), { signal });
      this.sync();
    }
    restorePickerFocus() {
      const input = document.getElementById(this.lastPickerId);
      // Dawn targets the option ID after replacing the picker; focus its select.
      if (input?.matches('select, [data-phone-family]') && (document.activeElement === this.lastPickerElement || document.activeElement === document.body)) {
        input.focus({ preventScroll: true });
      }
    }
    showVariantError() {
      this.root.removeAttribute('aria-busy');
      const error = this.root.querySelector('[data-variant-error]');
      if (error) error.hidden = false;
      this.button.disabled = true;
      this.sync();
    }
    sync() {
      if (!this.button) return;
      const excluded = this.root.querySelector('[data-excluded-selection]');
      if (excluded && this.button.disabled) {
        const label = this.button.querySelector('span');
        if (label && label.textContent !== excluded.textContent) label.textContent = excluded.textContent;
      }
      if (!this.button.disabled) this.root.removeAttribute('aria-busy');
      const priceContainer = this.root.querySelector('[id^="price-"]');
      const price = priceContainer?.querySelector('.price--on-sale .price-item--sale') || priceContainer?.querySelector('.price__regular .price-item--regular');
      if (!this.button.disabled && this.button.getAttribute('aria-disabled') !== 'true' && price) {
        const label = this.button.querySelector('span');
        const nextLabel = `${this.dataset.addLabel} — ${price.textContent.trim()}`;
        if (label && label.textContent !== nextLabel) label.textContent = nextLabel;
      }
      if (!this.bar) return;
      const focusedField = this.root.contains(document.activeElement) && document.activeElement.matches('input:not([type=radio]), textarea, select');
      this.bar.hidden = !this.pastButton || focusedField || Boolean(window.Shopify?.designMode);
      // This is navigation, not an add-to-cart button. Keep it usable when the
      // current variant is sold out so the shopper can choose another model.
      const selection = this.root.querySelector('.strayzy-product__selection strong');
      const stickyVariant = this.bar.querySelector('[data-sticky-variant]');
      if (stickyVariant && selection) stickyVariant.textContent = selection.textContent;
      this.bar.querySelector('[data-sticky-price]').textContent = priceContainer?.classList.contains('hidden') ? '' : price?.textContent.trim() || '';
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.unsubscribe?.();
      this.unsubscribeOptions?.();
      this.stateObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      clearTimeout(this.failureTimer);
    }
  });
}

// Optional screen protector follows the native case model and joins one cart request.
if (!customElements.get('strayzy-glass-addon')) {
  customElements.define('strayzy-glass-addon', class extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('product-info');
      this.checkbox = this.querySelector('[data-glass-check]');
      this.modelSelect = this.querySelector('[data-glass-model-select]');
      this.variants = JSON.parse(this.querySelector('[data-glass-variants]').textContent);
      this.caseModels = JSON.parse(this.querySelector('[data-case-models]').textContent);
      this.controller = new AbortController();
      this.root.addEventListener('change', () => this.sync(), { signal: this.controller.signal });
      this.root.addEventListener('submit', event => this.addBundle(event), { capture: true, signal: this.controller.signal });
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({data}) => {
        if (data.sectionId === this.dataset.section) this.sync();
      });
      this.sync();
    }
    normalize(value) { return String(value || '').trim().toLowerCase().replace(/\s+/g, ' '); }
    sync() {
      const form = document.getElementById(this.dataset.formId);
      const caseModel = this.caseModels[form?.querySelector('[name="id"]')?.value];
      const displayedModel = this.root.querySelector('strayzy-phone-model select')?.value || caseModel;
      const match = this.variants.find(variant => this.normalize(variant.model) === this.normalize(displayedModel));
      if (this.modelSelect) {
        if (this.lastCaseModel !== this.normalize(displayedModel)) {
          this.modelSelect.value = match?.available ? String(match.id) : '';
          this.lastCaseModel = this.normalize(displayedModel);
        }
        this.modelSelect.disabled = false;
      }
      const chosen = this.modelSelect
        ? this.variants.find(variant => String(variant.id) === this.modelSelect.value)
        : match;
      this.selectedVariant = chosen?.available && this.normalize(caseModel) === this.normalize(displayedModel) ? chosen : null;
      this.checkbox.disabled = !chosen?.available;
      if (!chosen?.available) this.checkbox.checked = false;
      const modelLabel = this.querySelector('[data-glass-model]');
      if (modelLabel) modelLabel.textContent = displayedModel || 'Pasirink telefono modelį';
      if (chosen) this.querySelector('[data-glass-price]').textContent = chosen.price;
      this.querySelector('[data-glass-note]').textContent = chosen?.available
        ? 'Apsaugok ir savo telefono ekraną'
        : 'Pasirink stikliuko modelį';
    }
    async addBundle(event) {
      if (event.target.getAttribute('id') !== this.dataset.formId) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.pending) return;
      this.sync();
      const error = this.querySelector('[data-glass-error]');
      error.hidden = true;
      const button = event.target.querySelector('[type="submit"]');
      if ((this.checkbox.checked && !this.selectedVariant) || button.disabled || this.root.getAttribute('aria-busy') === 'true') {
        error.textContent = 'Palauk, kol bus parinktas telefono modelis, ir bandyk dar kartą.';
        error.hidden = false;
        return;
      }
      this.pending = true;
      const spinner = button.querySelector('.loading__spinner');
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('loading');
      spinner?.classList.remove('hidden');
      try {
        const formData = new FormData(event.target);
        if (this.checkbox.checked) this.appendItems(formData);
        formData.set('sections', 'cart-icon-bubble');
        formData.set('sections_url', window.location.pathname);
        const response = await fetch(routes.cart_add_url, {
          method: 'POST', headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: formData
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || 'Nepavyko pridėti prekių į krepšelį.');
        this.showAdded(result, button);
      } catch (failure) {
        error.textContent = failure.message || 'Nepavyko pridėti prekių. Patikrink krepšelį prieš bandydamas dar kartą.';
        error.hidden = false;
      } finally {
        this.pending = false;
        button.removeAttribute('aria-disabled');
        button.classList.remove('loading');
        spinner?.classList.add('hidden');
      }
    }

    showAdded(result, trigger) {
      const dialog = document.createElement('dialog');
      dialog.className = 'strayzy-added';
      dialog.setAttribute('aria-label', 'Pridėta į krepšelį');
      dialog.innerHTML = `<style>
        .strayzy-added{position:fixed;inset:auto 20px 20px auto;margin:0;width:360px;max-width:calc(100vw - 24px);max-height:calc(100dvh - 32px);overflow:auto;border:1px solid #e9e5e9;border-radius:22px;padding:20px;background:#fff;color:#17121f;box-shadow:0 12px 50px #170c2b26;font-family:var(--strayzy-font-body,var(--font-body-family,sans-serif));font-size:14px;font-weight:400;letter-spacing:0;line-height:1.45}
        .strayzy-added::backdrop{background:rgba(20,15,25,.38)}
        .strayzy-added__head{display:flex;align-items:center;gap:8px;margin-bottom:16px;font-size:18px;font-weight:700}
        .strayzy-added__tick{color:#008774;font-size:22px}
        .strayzy-added__close{margin-left:auto;border:0;background:#f3f1f3;border-radius:50%;width:36px;height:36px;font-family:inherit;font-size:24px;cursor:pointer;color:inherit}
        .strayzy-added__item{display:flex;gap:12px;align-items:center;border:1px solid #eeebee;border-radius:12px;padding:10px;margin:8px 0}
        .strayzy-added__item img{width:54px;height:70px;object-fit:contain;flex-shrink:0}
        .strayzy-added__item strong{display:block;font-size:15px;line-height:1.3}
        .strayzy-added__item small{display:block;color:#756e7b;font-size:14px;line-height:1.4;margin:3px 0}
        .strayzy-added__price{font-size:15px;font-weight:700}
        .strayzy-added__actions{display:grid;gap:8px;margin-top:18px}
        .strayzy-added__actions button,.strayzy-added__actions a{display:block;width:100%;border:1px solid #170c2b;border-radius:30px;padding:12px 16px;font-family:inherit;font-size:15px;font-weight:600;text-align:center;text-decoration:none;cursor:pointer;background:white;color:#170c2b;line-height:1.4}
        .strayzy-added__actions .strayzy-added__primary{background:#170c2b;color:white}
        .strayzy-added__actions .strayzy-added__cart{border:0;text-decoration:underline;color:#756e7b;padding:4px}
        .strayzy-added__shipping{margin-top:12px;padding:12px;border-radius:12px;background:#ffe2ed;color:#251329;font-size:14px;line-height:1.45}
        .strayzy-added__shipping strong,.strayzy-added__shipping span{display:block}
        .strayzy-added__shipping span{margin-top:4px;color:#71556a}
        .strayzy-added__shipping progress{display:block;width:100%;height:6px;margin-top:9px;border:0;border-radius:8px;overflow:hidden;appearance:none;background:#efc5d7}
        .strayzy-added__shipping progress::-webkit-progress-bar{background:#efc5d7;border-radius:8px}
        .strayzy-added__shipping progress::-webkit-progress-value{background:#ea7fb0;border-radius:8px}
        .strayzy-added__shipping progress::-moz-progress-bar{background:#ea7fb0}
        .strayzy-added__shipping.is-free{background:#e5f5ee}
        .strayzy-added__shipping.is-free progress::-webkit-progress-value{background:#008774}
        .strayzy-added__glass{margin-top:12px;padding:12px;background:#f8f6fa;border:1px solid #e5dfea;border-radius:12px;font-size:14px;line-height:1.4}
        .strayzy-added__glass>.strayzy-added__item{border:0;padding:0;margin:10px 0;background:transparent}
        .strayzy-added__glass select{display:block;max-width:100%;width:100%;margin-top:5px;border:1px solid #ded8e2;border-radius:8px;padding:5px;font-family:inherit;font-size:14px;background:white;color:#17121f}
        .strayzy-added__glass .strayzy-added__item>div{min-width:0}
        .strayzy-added__glass button{width:100%;border:1px solid #170c2b;border-radius:20px;padding:9px;background:white;color:#170c2b;font-family:inherit;font-size:14px;font-weight:600;cursor:pointer}
        .strayzy-added__glass button:disabled{cursor:default;opacity:.65}
        .strayzy-added__glass p{margin:6px 0 0;font-size:14px}
        .strayzy-added__glass p:empty{display:none}
        .strayzy-added [hidden]{display:none!important}
        .strayzy-added :focus-visible{outline:2px solid #4b18ff;outline-offset:3px}
        @media(max-width:600px){.strayzy-added{right:12px;bottom:max(12px,env(safe-area-inset-bottom));padding:16px}}
      </style><div class="strayzy-added__head"><span class="strayzy-added__tick" aria-hidden="true">✓</span>Pridėta į krepšelį<button type="button" class="strayzy-added__close" aria-label="Uždaryti">×</button></div><div data-added-items></div><div class="strayzy-added__shipping" aria-live="polite"><strong data-shipping-title>Nemokamas pristatymas nuo 50 €</strong><span data-shipping-detail></span><progress max="5000" value="0" aria-label="Iki nemokamo pristatymo" hidden></progress></div><div class="strayzy-added__actions"><a data-choose-case class="strayzy-added__primary" hidden>Išsirink dar vieną dėkliuką</a><button type="button" class="strayzy-added__primary" data-continue>Tęsti apsipirkimą</button><form method="post"><button type="submit" name="checkout">Pereiti prie atsiskaitymo</button></form><a class="strayzy-added__cart">Peržiūrėti krepšelį</a></div>`;
      dialog.querySelector('form').action = routes.cart_url;
      dialog.querySelector('.strayzy-added__cart').href = routes.cart_url;
      dialog.querySelector('[data-choose-case]').href = Array.from(this.root.querySelectorAll('a')).find(link => link.textContent.includes('Visi dizainai'))?.href || '/collections/all';
      const money = value => new Intl.NumberFormat(document.documentElement.lang || 'lt', {style:'currency',currency:window.Shopify?.currency?.active || 'EUR'}).format(typeof value === 'string' && value.includes('.') ? Number(value) : value / 100);
      for (const item of result.items || [result]) {
        const row = document.createElement('div');
        row.className = 'strayzy-added__item';
        const image = document.createElement('img');
        image.src = item.featured_image?.url || item.image || (String(item.variant_id || item.id) === String(this.selectedVariant?.id) ? this.querySelector('.strayzy-glass__image')?.src : this.root.querySelector('.product__media-item.is-active img, .product__media img')?.src) || '';
        image.alt = item.product_title || item.title;
        if (image.getAttribute('src')) row.append(image);
        const details = document.createElement('div');
        const title = document.createElement('strong');
        title.textContent = item.product_title || item.title;
        const variant = document.createElement('small');
        variant.textContent = [item.variant_title, 'Kiekis: ' + item.quantity].filter(Boolean).join(' · ');
        const price = document.createElement('span');
        price.className = 'strayzy-added__price';
        price.textContent = money(item.final_line_price ?? item.line_price ?? item.price * item.quantity);
        details.append(title, variant, price); row.append(details);
        dialog.querySelector('[data-added-items]').append(row);
      }
      const close = () => dialog.close();
      dialog.querySelector('.strayzy-added__close').addEventListener('click', close);
      dialog.querySelector('[data-continue]').addEventListener('click', close);
      dialog.addEventListener('click', event => {
        const rect = dialog.getBoundingClientRect();
        if (event.target === dialog && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom)) close();
      });
      dialog.addEventListener('close', () => { dialog.remove(); trigger?.focus({preventScroll:true}); }, {once:true});
      document.body.append(dialog); dialog.showModal();
      const refreshShipping = () => fetch(routes.cart_url + '.js', {headers:{Accept:'application/json'},cache:'no-store'})
        .then(response => { if (!response.ok) throw new Error('Cart unavailable'); return response.json(); })
        .then(cart => {
          if (!dialog.isConnected || cart.currency !== 'EUR') return;
          const total = typeof cart.total_price === 'string' && cart.total_price.includes('.') ? Math.round(Number(cart.total_price) * 100) : Number(cart.total_price);
          if (!Number.isFinite(total)) return;
          const remaining = Math.max(0, 5000 - total);
          const shipping = dialog.querySelector('.strayzy-added__shipping');
          shipping.classList.toggle('is-free', remaining === 0);
          dialog.querySelector('[data-shipping-title]').textContent = remaining === 0 ? '✓ Tau priklauso nemokamas pristatymas!' : 'Pridėk dar vieną dėkliuką ir gauk nemokamą pristatymą';
          dialog.querySelector('[data-shipping-detail]').textContent = remaining === 0 ? 'Tavo krepšelis jau pasiekė 50 €.' : 'Iki nemokamo pristatymo trūksta tik ' + money(remaining) + '.';
          const progress = dialog.querySelector('progress');
          progress.value = Math.min(5000, Math.max(0,total)); progress.hidden = false;
          dialog.querySelector('[data-choose-case]').hidden = remaining === 0;
          dialog.querySelector('[data-continue]').hidden = remaining > 0;
        }).catch(() => {});
      refreshShipping();

      if (!this.checkbox.checked && this.variants.some(variant => variant.available)) {
        const offer = document.createElement('div');
        offer.className = 'strayzy-added__glass';
        offer.innerHTML = '<strong>Apsaugok ir savo telefono ekraną</strong><div class="strayzy-added__item"><img alt="Apsauginis telefono stikliukas"><div><strong>Apsauginis telefono stikliukas</strong><span data-offer-price></span><select aria-label="Stikliuko modelis išokančiame lange"></select></div></div><button type="button" data-offer-add>Pridėti stikliuką</button><p role="status" data-offer-status></p>';
        offer.querySelector('img').src = this.querySelector('.strayzy-glass__image')?.src || '';
        const select = offer.querySelector('select');
        for (const variant of this.variants) {
          const option = document.createElement('option');
          option.value = variant.id; option.textContent = variant.model; option.disabled = !variant.available;
          select.append(option);
        }
        select.value = String(this.selectedVariant?.id || this.variants.find(variant => variant.available).id);
        const updatePrice = () => { offer.querySelector('[data-offer-price]').textContent = this.variants.find(variant => String(variant.id) === select.value)?.price || ''; };
        select.addEventListener('change', updatePrice); updatePrice();
        dialog.querySelector('.strayzy-added__shipping').before(offer);
        const add = offer.querySelector('[data-offer-add]');
        add.addEventListener('click', async () => {
          if (add.disabled) return;
          const chosen = this.variants.find(variant => String(variant.id) === select.value && variant.available);
          if (!chosen) return;
          add.disabled = true; select.disabled = true; add.textContent = 'Pridedama…';
          const status = offer.querySelector('[data-offer-status]'); status.textContent = '';
          try {
            const data = new FormData(); data.set('id',chosen.id); data.set('quantity','1');
            data.set('sections','cart-icon-bubble'); data.set('sections_url',window.location.pathname);
            const response = await fetch(routes.cart_add_url,{method:'POST',headers:{Accept:'application/json','X-Requested-With':'XMLHttpRequest'},body:data});
            const added = await response.json();
            if (!response.ok || added.status) throw new Error(added.description || 'Nepavyko pridėti stikliuko.');
            add.textContent = '✓ Stikliukas pridėtas';
            status.textContent = chosen.model + ' stikliukas jau krepšelyje.';
            const badge = document.getElementById('cart-icon-bubble');
            const html = added.sections?.['cart-icon-bubble'];
            if (badge && html) badge.innerHTML = new DOMParser().parseFromString(html,'text/html').querySelector('.shopify-section')?.innerHTML || html;
            refreshShipping();
          } catch (failure) {
            status.textContent = failure.message || 'Patikrink krepšelį prieš bandydamas dar kartą.';
            add.textContent = 'Pridėti stikliuką'; add.disabled = false; select.disabled = false;
          }
        });
      }
      const badge = document.getElementById('cart-icon-bubble');
      const section = result.sections?.['cart-icon-bubble'];
      if (badge && section) badge.innerHTML = new DOMParser().parseFromString(section, 'text/html').querySelector('.shopify-section')?.innerHTML || section;
    }
    appendItems(formData) {
      const entries = [...formData.entries()];
      for (const [key] of entries) formData.delete(key);
      for (const [key, value] of entries) {
        const property = key.match(/^properties\[(.*)\]$/);
        if (property) formData.append('items[0][properties][' + property[1] + ']', value);
        else if (['id', 'quantity', 'selling_plan'].includes(key)) formData.append('items[0][' + key + ']', value);
        else formData.append(key, value);
      }
      formData.append('items[1][id]', this.selectedVariant.id);
      formData.append('items[1][quantity]', String(parseInt(entries.find(([key]) => key === 'quantity')?.[1]) || 1));
    }
    disconnectedCallback() { this.controller?.abort(); this.unsubscribe?.(); }
  });
}
